import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { ShopContext } from '../context/ShopContext';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Paper, 
  Grid, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImageIcon from '@mui/icons-material/Image';

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { currency } = useContext(ShopContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await api.get(`/api/orders/${orderId}`);
        setOrder(response.data);
      } catch (err) {
        setError('Failed to load order details. Please try again later.');
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  const getStatusChip = (status) => {
    const statusColors = {
      'Processing': 'warning',
      'Shipped': 'info',
      'Delivered': 'success',
      'Cancelled': 'error'
    };

    return (
      <Chip 
        label={status} 
        color={statusColors[status] || 'default'} 
        size="small"
        sx={{ ml: 1 }}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  }

  if (!order) {
    return <Alert severity="info" sx={{ m: 3 }}>Order not found.</Alert>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back to Orders
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Order #{order.id}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ mr: 1 }}>Status:</Typography>
          {getStatusChip(order.status)}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Order Items */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
              Order Items
            </Typography>
            <List>
              {order.items.map((item) => (
                <React.Fragment key={`${item.product_id}-${item.size}`}>
                  <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                    <ListItemAvatar>
                      <Avatar 
                        variant="square" 
                        src={item.product_images?.[0]} 
                        alt={item.product_name}
                        sx={{ 
                          width: 80, 
                          height: 80, 
                          mr: 2, 
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          '& img': {
                            objectFit: 'cover',
                            width: '100%',
                            height: '100%'
                          }
                        }}
                        imgProps={{
                          loading: 'lazy'
                        }}
                      >
                        {!item.product_images?.[0] && (
                          <ImageIcon sx={{ color: 'text.secondary', width: 40, height: 40 }} />
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography component="span" variant="body1" fontWeight="bold" display="block">
                          {item.product_name}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography component="span" variant="body2" display="block" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            Size: {item.size} | Qty: {item.quantity}
                          </Typography>
                          <Typography component="span" variant="body2" display="block" fontWeight="bold">
                            {currency}{item.price}
                          </Typography>
                        </>
                      }
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
              Order Summary
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography component="div" variant="body1">Subtotal:</Typography>
                <Typography component="div" variant="body1">{currency}{order.total_amount}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography component="div" variant="body1">Shipping:</Typography>
                <Typography component="div" variant="body1">Free</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {currency}{order.total_amount}
                </Typography>
              </Box>
            </Box>
          </Paper>


          {/* Shipping Information */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocalShippingIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" component="h2" fontWeight="bold">Shipping Information</Typography>
            </Box>
            <Typography>{order.customer_name}</Typography>
            <Typography>{order.shipping_address}</Typography>
            <Typography>
              {order.shipping_city}, {order.shipping_state} {order.shipping_zip_code}
            </Typography>
            <Typography>{order.shipping_country}</Typography>
            <Typography sx={{ mt: 1 }}>Phone: {order.customer_phone}</Typography>
            <Typography>Email: {order.customer_email}</Typography>
          </Paper>

          {/* Order Status */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">Order Status</Typography>
            </Box>
            <Box sx={{ position: 'relative', pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              {['Processing', 'Shipped', 'Delivered'].map((status, index) => (
                <Box key={status} sx={{ mb: 2, position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -8,
                      top: 4,
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      bgcolor: order.status === status 
                        ? 'primary.main' 
                        : order.status === 'Delivered' || 
                          (order.status === 'Shipped' && index < 2) ||
                          (order.status === 'Processing' && index === 0)
                          ? 'primary.main'
                          : '#e0e0e0',
                      zIndex: 1,
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      ml: 3, 
                      fontWeight: order.status === status ? 'bold' : 'normal',
                      color: order.status === status ? 'primary.main' : 'text.primary'
                    }}
                  >
                    {status}
                  </Typography>
                  {status === 'Shipped' && order.tracking_number && (
                    <Typography variant="body2" sx={{ ml: 3, color: 'text.secondary' }}>
                      Tracking: {order.tracking_number}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetails;
